import React, { useEffect, useState } from "react";
import { getFxStatus, refreshFx, money } from "@/lib/apiClient";
import { toast } from "sonner";
import { Coins, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAMES = { GBP: "British Pound", USD: "US Dollar", EUR: "Euro", AUD: "Australian Dollar", CAD: "Canadian Dollar", CHF: "Swiss Franc", JPY: "Japanese Yen", INR: "Indian Rupee", SGD: "Singapore Dollar", NZD: "New Zealand Dollar", SEK: "Swedish Krona", NOK: "Norwegian Krone", DKK: "Danish Krone", PLN: "Polish Zloty", ZAR: "South African Rand", HKD: "Hong Kong Dollar", CNY: "Chinese Yuan", MXN: "Mexican Peso", BRL: "Brazilian Real" };

export const CurrencySettings = ({ s, setS, onSave, saving }) => {
  const [fx, setFx] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => getFxStatus().then(setFx);
  useEffect(() => { load(); }, [s.base_currency]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await refreshFx(true);
      toast.success(`Re-rated ${r.converted} invoice(s) into ${r.base_currency}${r.unavailable ? ` · ${r.unavailable} without a rate` : ""}`);
      load();
    } catch {
      toast.error("Could not refresh rates");
    }
    setRefreshing(false);
  };

  const options = fx?.supported || Object.keys(NAMES);

  return (
    <div className="rounded-xl border border-border bg-card p-6 max-w-2xl" data-testid="currency-settings">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10 text-sky-400">
          <Coins className="h-5 w-5" />
        </span>
        <div>
          <div className="font-medium">Base currency & FX</div>
          <div className="text-sm text-muted-foreground">
            Foreign invoices are converted at the ECB daily rate on the invoice date. Dashboard totals, spend charts, supplier insights and the
            auto-approve limit use the base-currency value; the original amount is always kept.
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Base currency</label>
          <Select value={s.base_currency} onValueChange={(v) => setS({ ...s, base_currency: v })}>
            <SelectTrigger data-testid="base-currency-select" className="mt-1.5 font-mono"><SelectValue /></SelectTrigger>
            <SelectContent>
              {options.map((c) => (
                <SelectItem key={c} value={c} data-testid={`base-currency-option-${c}`}>
                  <span className="font-mono">{c}</span>{NAMES[c] ? <span className="text-muted-foreground ml-2 text-xs">{NAMES[c]}</span> : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2 mt-0 sm:mt-6">
          <div>
            <div className="text-sm font-medium">Auto-approve limit in base currency</div>
            <div className="text-xs text-muted-foreground">Compare the converted total against {money(s.auto_approve_max_amount, s.base_currency)}</div>
          </div>
          <Switch data-testid="approve-in-base-toggle" checked={!!s.auto_approve_in_base} onCheckedChange={(v) => setS({ ...s, auto_approve_in_base: v })} />
        </div>
      </div>

      {fx && (
        <div className="mt-4 grid grid-cols-3 gap-3 text-sm" data-testid="fx-status">
          <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Foreign invoices</div><div className="font-mono font-semibold" data-testid="fx-foreign-count">{fx.foreign_count}</div></div>
          <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Converted</div><div className="font-mono font-semibold" data-testid="fx-converted-count">{fx.converted}/{fx.total}</div></div>
          <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Currencies seen</div><div className="font-mono text-xs mt-1" data-testid="fx-currencies">{fx.currencies.join(", ") || "—"}</div></div>
        </div>
      )}

      <div className="border-t border-border pt-4 mt-4 flex flex-wrap justify-end gap-2">
        <Button data-testid="fx-refresh-btn" variant="ghost" onClick={refresh} disabled={refreshing}>
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Re-rate all invoices
        </Button>
        <Button data-testid="save-currency-btn" variant="outline" onClick={() => onSave("Currency settings saved")} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save Currency Settings
        </Button>
      </div>
    </div>
  );
};
